import DropboxGlyph from 'assets/logos/dropbox.svg?react';
import ReplitGlyph from 'assets/logos/replit.svg?react';
import {t} from 'i18next';
import {Fragment} from 'react';

import styles from './Apps.module.css';

/**
 * Splits a name at camelCase and underscore boundaries so long joined names
 * ("TerribleFrenchApplications") can wrap there. The text itself is unchanged; hyphens and
 * spaces already wrap on their own.
 */
export const getCoverSegments = (name: string): string[] =>
  name.split(/(?<=[a-z])(?=[A-Z])|(?<=_)/).filter(Boolean);

/** An app name that can wrap inside long joined words (see `getCoverSegments`). */
export const WrappableName = ({name}: {name: string}) => (
  <>
    {getCoverSegments(name).map((segment, index) => (
      <Fragment key={index}>
        {index > 0 ? <wbr /> : null}
        {segment}
      </Fragment>
    ))}
  </>
);

export type AppHost = 'dropbox' | 'replit' | 'vibebox';

// A faint mark of where the app is hosted, in place of the old platform badges.
const HostMark = ({host}: {host: AppHost}) =>
  host === 'replit' ? (
    <ReplitGlyph aria-hidden className={styles.appCoverGlyph} />
  ) : host === 'vibebox' ? (
    <span aria-hidden className={`${styles.appCoverGlyph} ${styles.appCoverGlyphText}`}>
      {t('apps_vibebox_badge_symbol')}
    </span>
  ) : (
    <DropboxGlyph aria-hidden className={styles.appCoverGlyph} />
  );

/**
 * Stand-in for apps without a screenshot: a calm surface with a faint mark of the app's host.
 * Tiles lay the name and author over it; small list thumbnails show the first letter.
 */
export const AppCover = ({
  host = 'dropbox',
  name,
  variant,
}: {
  host?: AppHost;
  name: string;
  variant: 'feed' | 'tile';
}) => (
  <span data-testid="app-cover" className={styles.appCover}>
    {variant === 'feed' ? (
      <span className={styles.appCoverText}>{name.trim().charAt(0).toUpperCase()}</span>
    ) : (
      <HostMark host={host} />
    )}
  </span>
);
